import type { Transaction } from "@rocicorp/zero";
import { type Principal, userFacingError } from "../authorization";
import { type Schema, zql } from "../generatedDrizzleSchema";
import { personalWorkAllowsSpacePermission } from "../personalWorkGuard";
import { grantsForScope } from "./grantQueries";
import {
  type Permission,
  TENANT_READ_FALLBACK,
  TENANT_WRITE_FALLBACK,
} from "./permissions";
import { grantHasPermission } from "./roles";

type AccessCheck = {
  tx: Pick<Transaction<Schema>, "run">;
  userId: string;
  // Tenant-scoped grants are keyed by the tenants.id, so tenant-level checks
  // pass the caller's tenant id as the scopeId.
  scopeId: string;
  tenantId: string;
  permission: Permission;
  // When set, the check runs against this principal instead of `userId`.
  principal?: Principal;
};

export async function assertAccess(check: AccessCheck): Promise<void> {
  if (!(await hasAccess(check))) throw userFacingError("Access denied");
}

export async function hasAccess(check: AccessCheck): Promise<boolean> {
  const { tx, userId, scopeId, tenantId, permission, principal } = check;
  const actor: Principal = principal ?? { kind: "user", userId };

  if (!(await personalWorkAllowsSpacePermission(check))) return false;

  if (await hasGrant(tx, actor, tenantId, "allow_all", tenantId)) return true;

  if (await hasGrant(tx, actor, scopeId, permission, tenantId)) return true;

  // A tenant-wide permission (e.g. tenant:spaces_read, tenant:networks_manage)
  // subsumes the scoped permission it maps to, so it grants access without a
  // per-scope grant. The read map is shared with the ZQL `canRead*` predicates
  // and the Drizzle `accessExists`.
  const tenantWide =
    TENANT_READ_FALLBACK[permission] ?? TENANT_WRITE_FALLBACK[permission];
  return tenantWide
    ? hasGrant(tx, actor, tenantId, tenantWide, tenantId)
    : false;
}

async function hasGrant(
  tx: AccessCheck["tx"],
  actor: Principal,
  scopeId: string,
  permission: Permission,
  tenantId: string
): Promise<boolean> {
  // A service account matches only its own direct grants: no group membership,
  // no implicit tenant admin -- its authority is exactly what is granted to it.
  if (actor.kind === "serviceAccount") {
    const grants = await tx.run(
      grantsForScope(scopeId, tenantId).where(
        "serviceAccountId",
        actor.serviceAccountId
      )
    );
    for (const g of grants) {
      if (grantHasPermission(g, permission)) return true;
    }
    return false;
  }

  const userId = actor.userId;
  const directGrants = await tx.run(
    grantsForScope(scopeId, tenantId).where("userId", userId)
  );

  for (const g of directGrants) {
    if (grantHasPermission(g, permission)) return true;
  }

  const groups = await tx.run(
    zql.userGroups
      .where("tenantId", tenantId)
      .where(({ exists }) =>
        exists("roleGrantsAsScope", (mg) =>
          mg.where("userId", userId).where("tenantId", tenantId)
        )
      )
      .related("roleGrantsAsPrincipal", (g) =>
        g
          .where("scopeId", scopeId)
          .where("tenantId", tenantId)
          .related("customRole", (cr) => cr.related("rolePermissions"))
      )
  );

  for (const group of groups) {
    for (const g of group.roleGrantsAsPrincipal ?? []) {
      if (grantHasPermission(g, permission)) return true;
    }
  }

  return false;
}
